import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type {
  IxmUserauthV1JoinedRealmAccess,
  IxmUserauthV1OrganizationMemberState,
  IxmUserauthV1OrganizationState,
  IxmUserauthV1UserState,
} from '@interxfi/types/generated/api';
import {
  selectActiveAccess,
  selectActiveOrganizationMembership,
  selectContext,
  selectIsAuthenticated,
  selectIsAuthenticating,
  selectReset,
  selectSetActiveAccess,
  selectSetContext,
  selectSetIsAuthenticated,
  selectSetIsAuthenticating,
} from './selectors';

export interface OrganizationMembership {
  organizationMember?: IxmUserauthV1OrganizationMemberState;
  organization?: IxmUserauthV1OrganizationState;
}

export interface WhoAmIContext extends IxmUserauthV1UserState {
  hasMFA: boolean;
  organizationMemberships: OrganizationMembership[];
  accesses: IxmUserauthV1JoinedRealmAccess[];
  sessionExpiresAt?: Date;
  sessionId?: string;
}

export function realmAccessIdGetter(realmAccess: IxmUserauthV1JoinedRealmAccess) {
  return realmAccess?.access?.realmAccessId;
}

export type ContextChangeActiveAccessSelector = (
  context: WhoAmIContext | undefined,
  activeAccess: IxmUserauthV1JoinedRealmAccess | undefined,
) => IxmUserauthV1JoinedRealmAccess | undefined;

export function defaultContextChangeActiveAccessSelector(
  context: WhoAmIContext | undefined,
  activeAccess: IxmUserauthV1JoinedRealmAccess | undefined,
): IxmUserauthV1JoinedRealmAccess | undefined {
  if (context && activeAccess) {
    return context.accesses?.find((ctxAccess) => realmAccessIdGetter(ctxAccess) === realmAccessIdGetter(activeAccess));
  }

  return context?.accesses?.[0];
}

export interface RealmAuthStore {
  activeAccess: IxmUserauthV1JoinedRealmAccess | undefined;
  context: WhoAmIContext | undefined;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  setIsAuthenticating: (isAuthenticating: boolean) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setContext: (cb: (context: WhoAmIContext | undefined) => WhoAmIContext | undefined, isAuthenticating?: boolean) => WhoAmIContext | undefined;
  setActiveAccess: (realmAccessId: string) => IxmUserauthV1JoinedRealmAccess | undefined;
  reset(isAuthenticating?: boolean): void;
}

export const defaultInitialState = {
  activeAccess: undefined,
  context: undefined,
  isAuthenticating: true,
  isAuthenticated: false,
} as const;

export function createRealmAuthStore(
  initialState?: Partial<RealmAuthStore>,
  contextChangeActiveAccessSelector = defaultContextChangeActiveAccessSelector,
) {
  return create<RealmAuthStore>((set, get) => ({
    ...defaultInitialState,
    setIsAuthenticating: (isAuthenticating) => set({ isAuthenticating }),
    setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
    setContext: (cb) => {
      const { context: prevContext, activeAccess } = get();
      const result = cb(prevContext);

      set({
        context: result,
        activeAccess: result ? contextChangeActiveAccessSelector?.(result, activeAccess) : undefined,
      });

      return result;
    },
    setActiveAccess: (realmAccessId) => {
      const { activeAccess: prevAccess, context } = get();

      if (prevAccess && realmAccessId === realmAccessIdGetter(prevAccess)) {
        return prevAccess;
      }

      const access = context?.accesses?.find((ctxAccess) => realmAccessId === realmAccessIdGetter(ctxAccess));
      if (!access) {
        return;
      }

      set({ activeAccess: access });
      return access;
    },
    reset(isAuthenticating = true) {
      set({ ...defaultInitialState, ...initialState, isAuthenticating });
    },
    ...initialState,
  }));
}

export function createRealmAuthStoreHooks(
  initialState?: Partial<RealmAuthStore>,
  contextChangeActiveAccessSelector = defaultContextChangeActiveAccessSelector,
) {
  const useRealmAuthStore = createRealmAuthStore(initialState, contextChangeActiveAccessSelector);

  return {
    useRealmAuthStore,
    useRealmAuthActiveAccess: () => useRealmAuthStore(useShallow(selectActiveAccess)),
    useRealmAuthContext: () => useRealmAuthStore(useShallow(selectContext)),
    useRealmAuthIsAuthenticating: () => useRealmAuthStore(selectIsAuthenticating),
    useRealmAuthIsAuthenticated: () => useRealmAuthStore(selectIsAuthenticated),
    useRealmAuthReset: () => useRealmAuthStore(selectReset),
    useRealmAuthSetActiveAccess: () => useRealmAuthStore(selectSetActiveAccess),
    useRealmAuthSetContext: () => useRealmAuthStore(selectSetContext),
    useRealmAuthSetIsAuthenticated: () => useRealmAuthStore(selectSetIsAuthenticated),
    useRealmAuthSetIsAuthenticating: () => useRealmAuthStore(selectSetIsAuthenticating),
    useRealmAuthActiveOrganizationMembership: () => useRealmAuthStore(selectActiveOrganizationMembership),
  } as const;
}
